// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DateTimePickerRange.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component, PropTypes } from 'react';
import typeof { DOMElement } from 'react';
import { Boundary } from '@blueprintjs/core';
import classnames from 'classnames';
import DateTimePickerContainer from './Izenda.DateTimePickerContainer';
import Input from 'share/components/controls/Izenda.Input';
import { map, get, isNil, some } from 'lodash/fp';
import { observable } from 'mobx';
import Moment from 'moment';
import { reverseRangeValuesIfNeeded } from 'share/utils/Izenda.DateTimeUtils';
import { PickerDimension } from './Izenda.DateTimeConstants';
import DateTimePickerModel from 'logic/models/Izenda.DateTimePickerModel';
import connect from 'share/enhancers/Izenda.Connect';
import actions from './Izenda.DateTimePickerActions';

const START_DATE_INDEX = 0;
const END_DATE_INDEX = 1;

@connect(actions)
export default class DateTimePickerRange extends Component {
  static propTypes = {
    onChange: PropTypes.func.isRequired,
    visible: PropTypes.bool.isRequired,
    hidePicker: PropTypes.func.isRequired,
    containerProps: PropTypes.object.isRequired,
    value: PropTypes.array,
    onBlur: PropTypes.func,
    displayFormat: PropTypes.string,
    storeFormat: PropTypes.string,
    renderInContainer: PropTypes.bool,
    disabled: PropTypes.bool,
    name: PropTypes.string,
    placeholder: PropTypes.string
  };

  // Models for picker
  @observable models: Array<DateTimePickerModel> = null;

  // Current boundary to modify
  @observable boundaryToModify: string = null;

  /**
   * Constructor
   * @param {Object} props
   */
  constructor(props: Object) {
    super(props);
    this.models = [new DateTimePickerModel(), new DateTimePickerModel()];
    this.setFormats(props);
    this.setData(props);
  }

  /**
   * Component will receive props
   * @param {Object} nextProps
   */
  componentWillReceiveProps(nextProps: Object) {
    const { displayFormat: prevDisplayFormat, storeFormat: prevStoreFormat } = this.props;
    const { displayFormat, storeFormat } = nextProps;
    (prevDisplayFormat !== displayFormat || prevStoreFormat !== storeFormat) && this.setFormats(nextProps);
    this.setData(nextProps);
  }

  /**
   * Set data for models
   */
  setData(props: Object) {
    const { value } = props;
    this.models.forEach((model: DateTimePickerModel, index: number) =>
      model.setData(get(index, value))
    );
  }

  /**
   * Set formats for models
   */
  setFormats(props: Object) {
    const { displayFormat, storeFormat } = props;
    this.models.forEach((model: DateTimePickerModel) => model.setFormats(displayFormat, storeFormat));
  }

  /**
   * Set anchor for float affixed
   */
  setAnchor = (node: DOMElement) => {
    this.anchor = node;
  };

  /**
   * Focus an input
   * @param {number} index
   */
  focusInput(index: number) {
    const { onFocusInput } = this.props;
    this.boundaryToModify = index === START_DATE_INDEX ? Boundary.START : Boundary.END;
    onFocusInput(this.models[index])();
  }

  /**
   * Focus input
   * @param {number} index
   */
  onFocusInput = (index: number) => () => {
    this.focusInput(index);
  };

  /**
   * Save without reverse range value
   */
  saveWithoutReverseValue = () => {
    this.save(false);
  };

  /**
   * Save value
   * @param {boolean} reverseValue
   */
  save = (reverseValue: boolean = true) => {
    const { onChange } = this.props;

    let dateTimes = map(model => model.dateTime, this.models);
    reverseValue && (dateTimes = reverseRangeValuesIfNeeded(dateTimes));

    this.models.forEach((model: DateTimePickerModel, index: number) => model.setDateTime(dateTimes[index]));
    const hadChanged = some((model: DateTimePickerModel) => model.hadValueChanged(), this.models);

    hadChanged && onChange && onChange(map(model => model.getSavingValue(), this.models));
  };

  /**
   * On select a date in picker
   */
  onSelect = (dateTimes: Array<Moment>) => {
    this.models.forEach((model: DateTimePickerModel, index: number) => model.setDateTime(dateTimes[index]));

    // Focus to the remaining input
    if (!isNil(this.models[START_DATE_INDEX].dateTime) && isNil(this.models[END_DATE_INDEX].dateTime)) {
      this.focusInput(END_DATE_INDEX);
    } else if (isNil(this.models[START_DATE_INDEX].dateTime) && !isNil(this.models[END_DATE_INDEX].dateTime)) {
      this.focusInput(START_DATE_INDEX);
    }

    this.save();
  };

  /**
   * Render input for range picker
   * @param {number} index
   */
  renderInput(index: number) {
    const {
      disabled,
      name,
      placeholder,
      visible,
      onKeyDownInput,
      onChangeInput,
      setInputDOMNode,
      onBlurInput
    } = this.props;

    const model = this.models[index];
    const onFocusFunc = this.onFocusInput(index);
    const classes = classnames('izenda-DateTimePicker-input', {
      'select-boundary':
        this.boundaryToModify === (index === START_DATE_INDEX ? Boundary.START : Boundary.END) && visible
    });
    const inputValue = this.models[index].inputValue;

    return (
      <Input
        type='text'
        ref={setInputDOMNode(model)}
        value={inputValue}
        title={inputValue}
        onBlur={onBlurInput(model, this.save)}
        onKeyDown={onKeyDownInput(model, this.save)}
        onChange={onChangeInput(model, this.saveWithoutReverseValue)}
        placeholder={placeholder}
        name={name}
        className={classes}
        disabled={disabled}
        onClick={onFocusFunc}
        onFocus={onFocusFunc}
      />
    );
  }

  /**
   * Render 2 inputs for range picker
   */
  renderControl() {
    return (
      <div ref={this.setAnchor} className='izenda-FilterRangeInput'>
        {this.renderInput(START_DATE_INDEX)}
        <div className='izenda-Filter-BetweenIcon'>
          <span className='icon-big-arr' />
        </div>
        {this.renderInput(END_DATE_INDEX)}
      </div>
    );
  }

  /**
   * Render
   */
  render() {
    const calendarProps = {
      boundaryToModify: this.boundaryToModify,
      onSelect: this.onSelect,
      value: map(model => model.dateTime, this.models)
    };

    return (
      <DateTimePickerContainer
        anchor={this.anchor}
        pickerWidth={PickerDimension.RANGE_WIDTH}
        calendarProps={calendarProps}
        hasRange
        pickerAsPopover
        {...this.props.containerProps}
      >
        {this.renderControl()}
      </DateTimePickerContainer>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/DateTime/Izenda.DateTimePickerRange.js